/**
 * Structured rental inspections (api#861): the words this app puts on them
 * and the one place a POST body is built, so the Inspections register and
 * the field Delivery and Return screens send the same shape.
 *
 * The checklist itself is the server's (base list plus the category's family,
 * chosen per tenant). Overall result and asset condition are derived by the
 * server too; this file computes the overall only to TELL the reader before
 * they submit, never to send it.
 */
import type {
  RentalChecklistFamily, RentalChecklistResponse, RentalInspectionType, RentalItemResult, RentalOverall,
} from '../types/rental-inspection';

export const RENTAL_RESULTS: RentalItemResult[] = ['pass', 'minor', 'fail', 'na'];

export const RENTAL_RESULT_LABEL: Record<RentalItemResult, string> = {
  pass: 'Pass',
  minor: 'Minor',
  fail: 'Fail',
  na: 'N/A',
};

export const RENTAL_FAMILY_OPTIONS: { value: RentalChecklistFamily; label: string }[] = [
  { value: 'tanks', label: 'Tanks' },
  { value: 'pumps_power', label: 'Pumps and power units' },
  { value: 'generators_lights', label: 'Generators and light towers' },
  { value: 'pipe_iron_valves', label: 'Pipe, iron and valves' },
  { value: 'rig_mats', label: 'Rig mats' },
  { value: 'wellsite_trailers', label: 'Wellsite trailers and shacks' },
];

/** Answers keyed by item key. */
export type RentalAnswers = Record<string, { result: RentalItemResult | null; notes: string }>;

/** True when every item on the checklist has an answer. */
export function rentalChecklistComplete(cl: RentalChecklistResponse | undefined, a: RentalAnswers | undefined): boolean {
  if (!cl) return false;
  return cl.items.every((i) => !!a?.[i.key]?.result);
}

/** Mirrors the server: any Fail → fail, any Minor → minor, else pass; all N/A → null. */
export function rentalOverall(a: RentalAnswers | undefined): RentalOverall | null {
  const rs = Object.values(a ?? {}).map((x) => x.result).filter((r): r is RentalItemResult => !!r && r !== 'na');
  if (rs.length === 0) return null;
  if (rs.includes('fail')) return 'fail';
  if (rs.includes('minor')) return 'minor';
  return 'pass';
}

/** The `item_results` the server takes, in the checklist's own order. */
export function rentalItemResults(cl: RentalChecklistResponse, a: RentalAnswers) {
  return cl.items.map((i) => ({
    item: i.key,
    result: a[i.key]?.result as RentalItemResult,
    ...(a[i.key]?.notes?.trim() ? { notes: a[i.key].notes.trim() } : {}),
  }));
}

export interface StructuredInspectionExtras {
  /** Client-minted, only when photos were uploaded against it (api#1066). */
  id?: string;
  photo_urls?: string[];
  damage_photos?: string[];
  /** The dispatch this inspection was done on; absent in the yard (api#1068). */
  dispatch_id?: string;
  order_id?: string;
  inspector_name?: string;
  signed_by_name?: string;
  signature_url?: string | null;
  signed_at?: string;
  hours_reading?: number;
  damage_description?: string;
  checklist?: unknown;
}

/**
 * THE body for `POST /surface-rentals/inspections`, structured. No `condition`:
 * the server derives it from the results and refuses one that disagrees.
 * Kept to one builder so a later switch to the atomic deliver/return calls
 * changes where this body goes, not what it says.
 */
export function structuredInspectionBody(
  assetId: string,
  type: RentalInspectionType,
  cl: RentalChecklistResponse,
  answers: RentalAnswers,
  extras: StructuredInspectionExtras = {},
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    asset_id: assetId,
    inspection_type: type,
    item_results: rentalItemResults(cl, answers),
  };
  for (const [k, v] of Object.entries(extras)) if (v !== undefined) body[k] = v;
  return body;
}

/**
 * The photo half of an inspection body (api#1066): the client-minted `id` and
 * the confirmed upload keys. Empty when there are no photos, so the server
 * mints the id as before. An id sent with photos is the one they were
 * uploaded against; sent with none it would only claim an id for nothing.
 */
export function inspectionPhotoExtras(
  id: string,
  photos: string[],
  damagePhotos: string[] = [],
): Pick<StructuredInspectionExtras, 'id' | 'photo_urls' | 'damage_photos'> {
  if (photos.length === 0 && damagePhotos.length === 0) return {};
  return {
    id,
    ...(photos.length ? { photo_urls: photos } : {}),
    ...(damagePhotos.length ? { damage_photos: damagePhotos } : {}),
  };
}
